import api from '~/store/services/api';
import urlConstants from '~/lib/constants/url.constants';
import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';
import { ENUMERATION_CACHE_TIME } from '~/store/services/enumerations';
import { RefCarePlanManifestationResponse } from '~/lib/types/carePlan/RefCarePlanManifestationResponse';
import { RefCarePlanGoalResponse } from '~/lib/types/carePlan/RefCarePlanGoalResponse';
import { HttpRequestMethod } from '~/store';
import {
  GetInterventionsRequest,
  OrderedEnumeration,
  RefCarePlanInterventionResponse,
  RefCarePlanFrequencyResponse,
  ProviderTypeManifestResponse,
} from '~/lib/types/carePlan/carePlans';
import {
  RefCarePlanInterventionCategoryResponse,
  InterventionCategoriesAssigneesMap,
} from '~/lib/types/carePlan/customInterventions';

// Care plan reference data APIs
export const refCarePlanEnumsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getRefCarePlanProblemsEnum: build.query<
      RefCarePlanProblemResponse[],
      { diagnosisCodeLabels?: string[] }
    >({
      query: ({ diagnosisCodeLabels }) => ({
        url: urlConstants.carePlans.getRefCarePlanProblemsEnum(
          diagnosisCodeLabels,
        ),
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getRefCarePlanGoalsEnum: build.query<
      RefCarePlanGoalResponse[],
      { problemCodes?: string[] }
    >({
      query: ({ problemCodes }) => ({
        url: urlConstants.carePlans.getRefCarePlanGoalsEnum(problemCodes),
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getRefCarePlanManifestationsEnum: build.query<
      RefCarePlanManifestationResponse[],
      { problemCodes?: string[] }
    >({
      query: ({ problemCodes }) => ({
        url: urlConstants.carePlans.getRefCareManifestationsEnum(problemCodes),
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getRefCarePlanInterventionsEnum: build.query<
      RefCarePlanInterventionResponse[],
      GetInterventionsRequest
    >({
      query: ({ goalCodes, modalityCode }) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getRefCareInterventionsEnum(
          goalCodes,
          modalityCode,
        ),
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getRefCarePlanFrequenciesEnum: build.query<
      RefCarePlanFrequencyResponse[],
      void
    >({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getFrequencies,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getInterventionCategoriesEnum: build.query<
      RefCarePlanInterventionCategoryResponse[],
      void
    >({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getInterventionCategories,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getInterventionCategoriesAssigneesMap: build.query<
      InterventionCategoriesAssigneesMap,
      void
    >({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getInterventionCategoriesAssigneesMap,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getProviderTypeManifest: build.query<ProviderTypeManifestResponse, void>({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getProviderTypeManifest,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getInterventionVoidReasons: build.query<OrderedEnumeration[], void>({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getInterventionVoidReasons,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    // Map of ProblemTypeCode -> goal statuses valid for that problem type.
    getProblemTypesGoalStatusesMap: build.query<
      Record<string, OrderedEnumeration[]>,
      void
    >({
      query: () => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.carePlans.getProblemTypesGoalStatusesMap,
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
  }),
});

export const {
  useGetRefCarePlanProblemsEnumQuery,
  useGetRefCarePlanGoalsEnumQuery,
  useGetRefCarePlanManifestationsEnumQuery,
  useGetRefCarePlanInterventionsEnumQuery,
  useGetRefCarePlanFrequenciesEnumQuery,
  useGetInterventionCategoriesEnumQuery,
  useGetInterventionCategoriesAssigneesMapQuery,
  useGetProviderTypeManifestQuery,
  useGetInterventionVoidReasonsQuery,
  useGetProblemTypesGoalStatusesMapQuery,
} = refCarePlanEnumsApi;
